import {DataModificationsHeader} from 'audit/DataModifications';
import {Permissions} from 'auth/PermissionConstants';
import {Notation} from 'biojs/BioConstants';
import {ChemicalStructureInput} from 'chemistry/ChemistryConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {Bioentity, BioentitySummary} from 'folder-items/FileConstants';
import {ComplexPolymerInput} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {SchemaValidationState, ValidationExtras} from 'schemas/SchemaValidationConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {SearchParams, SearchResults} from 'search/SearchConstants';
import {postSearchRequest} from 'search/SearchDAO';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';

export interface BioentitySearchResponse extends SearchResults {
  fileSummariesById: {[id: string]: BioentitySummary};
  permissionsByModelId: {[id: string]: Permissions};
  bioentityAliasInfosByBioentityId: {[bioentityId: string]: Array<BioentityAliasInfo>};
}
export function listBioentities(searchParams: SearchParams): Promise<BioentitySearchResponse> {
  return postSearchRequest({
    url: '/1/api/registries:list-bioentities',
    data: searchParams,
  });
}

export function bulkGetAliases(
  fileIds: Array<string>
): Promise<{[fileId: string]: Array<BioentityAliasInfo>}> {
  return net.postJSONPromise({
    url: '/1/api/inventory-files/actions/bulk-get-aliases',
    data: {fileIds},
  });
}

export function addAlias(fileId: string, alias: string): Promise<{alias: string}> {
  return net.postJSONPromise({
    url: `/1/api/inventory-files/${fileId}/actions/add-alias`,
    data: {alias},
  });
}

export function deleteAlias(fileId: string, alias: string): JQueryPromise<{}> {
  return net.postJSON({
    url: `/1/api/inventory-files/${fileId}/actions/delete-alias`,
    data: {alias},
  });
}

export function updateAlias(
  fileId: string,
  originalAlias: string,
  newAlias: string,
  dataModsHeader?: DataModificationsHeader
): Promise<{}> {
  return net.postJSONPromise({
    url: `/1/api/inventory-files/${fileId}/actions/update-alias`,
    data: {originalAlias, newAlias},
    extraHeaders: dataModsHeader,
  });
}

export interface UpdateRegistryCollectionsResponse {
  [apiIdentifier: string]: {
    bioentityAliasInfos: Array<BioentityAliasInfo>;
    name: string;
  };
}

export function updateRegistryCollections(
  fileIds: Array<string>,
  {
    inOrgCollection,
    inPersonalCollection,
  }: {inOrgCollection: Optional<boolean>; inPersonalCollection: Optional<boolean>}
): JQueryPromise<UpdateRegistryCollectionsResponse> {
  return net.postJSON({
    url: '/1/api/inventory-files/actions/update-registry-collections',
    data: {inOrgCollection, inUserCollection: inPersonalCollection, files: fileIds},
  });
}

export function getHoistedTags(
  fileId: string
): JQueryPromise<{hoistedTagsGraphById: {[pathId: string]: Array<unknown>}}> {
  return net.getJSON(`/1/api/inventory-files/${fileId}/hoisted-tags/`);
}

export interface CreateBioentityServerArgs {
  name: string;
  folderId: string;
  notation: Notation;
  residues: Optional<string>;
  chemicalStructureInput: Optional<ChemicalStructureInput>;
  tagSchemaId: string;
  tags: TagUpdateObject;
  studyIds: Optional<Array<string>>;
}

export async function create({
  name,
  folderId,
  tagSchemaId,
  tags,
  chemicalStructureInput,
  studyIds,
}: CreateBioentityServerArgs): Promise<Bioentity> {
  const resp = await net.postJSONPromise({
    url: '/1/api/bioentity',
    data: {
      name,
      folderId,
      tagSchemaId,
      tags,
      chemicalStructureInput,
      studyIds,
    },
  });
  FolderDAOCache.invalidateFolderContents(folderId);
  return resp.createdBioentity;
}

export interface CreateAndRegisterBioentityServerArgs {
  name: string;
  labelingStrategy: Optional<string>;
  projectFolderId: Optional<string>;
  notation: Notation;
  customSyntaxId: Optional<string>;
  residues: Optional<string>;
  chemicalStructureInput: Optional<ChemicalStructureInput>;
  tagSchemaId: string;
  tags: TagUpdateObject;
  inOrgCollection?: Optional<boolean>;
  inUserCollection?: Optional<boolean>;
  shouldErrorWhenMergingDuplicate: boolean;
  studyIds: Optional<Array<string>>;
  complexPolymerInput: Optional<ComplexPolymerInput>;
  dataModsHeader?: DataModificationsHeader;
}

export interface MergingDuplicateError {
  registeredDuplicateLabels: {
    name: string;
    entityRegistryId: string;
    apiId: string;
  };
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function isMergingDuplicateError(e: any): e is MergingDuplicateError {
  return net.isServerRequestError(e) && e.registeredDuplicateLabels != null;
}

/**
 * @param shouldErrorWhenMergingDuplicate when true will throw an error if the created bioentity is a
 * duplicate of some other bioentity in the registry. You can confirm that the thrown error is caused by
 * duplicate merging using `isMergingDuplicateError`
 */
export async function createAndRegister({
  inOrgCollection,
  inUserCollection,
  labelingStrategy,
  name,
  notation,
  customSyntaxId,
  projectFolderId,
  residues,
  tags,
  tagSchemaId,
  chemicalStructureInput,
  shouldErrorWhenMergingDuplicate,
  studyIds,
  complexPolymerInput,
  dataModsHeader,
}: CreateAndRegisterBioentityServerArgs): Promise<Bioentity> {
  return await TaskRunner.postJSONPromise({
    url: '/1/api/bioentities:create-in-registry',
    data: {
      inOrgCollection,
      inUserCollection,
      labelingStrategy,
      name,
      notation,
      customSyntaxId,
      projectFolderId,
      residues,
      tags,
      tagSchemaId,
      chemicalStructureInput,
      shouldErrorWhenMergingDuplicate,
      studyIds,
      complexPolymerInput,
    },
    extraHeaders: dataModsHeader,
  });
}

export function bulkGetValidationExtras(
  bioentityIds: Array<string>
): Promise<{validationExtrasByBioentityId: {[bioentityId: string]: ValidationExtras}}> {
  return net.postJSONPromise({
    url: '/1/api/bioentities:bulk-get-validation-extras',
    data: {bioentityIds},
  });
}

export function overrideValidationStatus(
  bioentityIds: Array<string>
): Promise<{schemaValidationStateByBioentityId: {[bioentityId: string]: SchemaValidationState}}> {
  return net.postJSONPromise({
    url: '/1/api/bioentities:override-validation-status',
    data: {bioentityIds},
  });
}

export function bulkOverrideValidationStatus(
  tagSchemaId: string,
  errorLabels: Array<string>
): JQueryPromise<{}> {
  return TaskRunner.postJSON({
    url: '/1/api/bioentities:bulk-override-validation-status',
    data: {tagSchemaId, errorLabels},
  });
}
